/**
 * «Анкета» — operator self-cabinet (SPEC §5). Authoring side of the public
 * dossier the client reads. Three sub-sections behind purple chip-segments:
 *   Профиль  — inline actor_profiles editor (PUT /api/operator/profile)
 *   Новости  — own posts + create/edit (POST/PUT /api/operator/news)
 *   Медиа    — upload + smart crop editor + filters + fan-only (the main feature)
 *
 * A status plaque (Черновик / На проверке у ОО / Опубликовано / На доработку)
 * is derived from profile.change + isDraft + autoApprove. «Отправить на
 * проверку» PUTs the profile; with autoApprove it lands as «Опубликовано».
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  useOperatorProfile, type ActorProfileFields, type ProfileResponse,
} from "../../hooks/useOperatorProfile";
import { ProfileTab } from "./ProfileTab";
import { NewsTab } from "./NewsTab";
import {
  AProfile, ANews, ASend, ACheck, AEye,
} from "./icons";
import css from "./anketa.module.css";

type Seg = "normal" | "fan" | "preview";

export function AnketaTab() {
  const pp = useOperatorProfile();
  const [seg, setSeg] = useState<Seg>("normal");
  const [toast, setToast] = useState<{ msg: string; err: boolean } | null>(null);
  const [restoreDraft, setRestoreDraft] = useState<ActorProfileFields | null>(null);
  const [draftBannerHidden, setDraftBannerHidden] = useState(false);

  const showToast = useCallback((msg: string, err = false) => {
    setToast({ msg, err });
    window.setTimeout(() => setToast((t) => (t && t.msg === msg ? null : t)), 3000);
  }, []);

  useEffect(() => {
    if (pp.draftFields) setDraftBannerHidden(false);
  }, [pp.draftFields]);

  if (pp.loading && !pp.data) {
    return <div className={css.wrap}><div className={css.empty}>Загрузка анкеты…</div></div>;
  }
  if (pp.error && !pp.data) {
    return (
      <div className={css.wrap}>
        <div className={css.empty}>{pp.error}<br /><button type="button" className={css.linkBtn} onClick={pp.reload}>Повторить</button></div>
      </div>
    );
  }
  if (!pp.data) return <div className={css.wrap}><div className={css.empty}>Нет данных</div></div>;

  return (
    <div className={css.wrap}>
      <div className={css.anketaHead}>
        <StatusPlaque data={pp.data} />
        <SaveStatusChip status={pp.saveStatus} />
      </div>

      {pp.draftFields && !draftBannerHidden ? (
        <div className={css.draftRestore} role="status">
          <div>
            <strong>Восстановить несохранённые правки?</strong>
            <span>Нашёл черновик с прошлой сессии. Можно вернуть его в форму и дослать автосейвом.</span>
          </div>
          <div className={css.draftRestoreActions}>
            <button
              type="button"
              className={`${css.btn} ${css.primary}`}
              onClick={() => {
                setRestoreDraft(pp.draftFields);
                setDraftBannerHidden(true);
                setSeg("normal");
                showToast("Черновик применён — автосейв досылает правки");
              }}
            >
              Восстановить
            </button>
            <button
              type="button"
              className={css.btn}
              onClick={() => {
                pp.dismissDraft();
                setDraftBannerHidden(true);
              }}
            >
              Отклонить
            </button>
          </div>
        </div>
      ) : null}

      <div className={css.segs} role="tablist">
        <button type="button" role="tab" aria-selected={seg === "normal"} className={`${css.seg} ${seg === "normal" ? css.segOn : ""}`} onClick={() => setSeg("normal")}>
          <AProfile /> Обычная
        </button>
        <button type="button" role="tab" aria-selected={seg === "fan"} className={`${css.seg} ${seg === "fan" ? css.segOn : ""}`} onClick={() => setSeg("fan")}>
          <ANews /> Для фанатов{pp.data.news.length ? <span className={css.segCount}>{pp.data.news.length}</span> : null}
        </button>
        <button type="button" role="tab" aria-selected={seg === "preview"} className={`${css.seg} ${seg === "preview" ? css.segOn : ""}`} onClick={() => setSeg("preview")}>
          <AEye /> Превью
        </button>
      </div>

      <ProfileSection key={pp.data.staffId + (pp.data.change?.id ?? "")} pp={pp} view={seg} restoreDraft={restoreDraft} onToast={showToast} />
      {seg === "fan" ? (
        <NewsTab news={pp.data.news} media={pp.data.media} onSave={pp.saveNews} onToast={showToast} />
      ) : null}

      {toast ? <div className={`${css.toast} ${toast.err ? css.err : ""}`}>{toast.msg}</div> : null}
    </div>
  );
}

/* ── Profile section: inline form + submit bar ─────────────────────────────── */
function ProfileSection({
  pp,
  view,
  restoreDraft,
  onToast,
}: {
  pp: ReturnType<typeof useOperatorProfile>;
  view: "normal" | "fan" | "preview";
  restoreDraft: ActorProfileFields | null;
  onToast: (msg: string, err?: boolean) => void;
}) {
  const collectRef = useRef<(() => ActorProfileFields) | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const registerCollect = useCallback((fn: () => ActorProfileFields) => { collectRef.current = fn; }, []);

  // Фоновое автосохранение из редактора: тихо, без reload и без пинга OO.
  const autoSave = useCallback(async (fields: ActorProfileFields): Promise<boolean> => {
    const res = await pp.saveProfile(fields, { auto: true });
    if (!res.ok) onToast(res.error ?? "Автосохранение не прошло", true);
    return res.ok;
  }, [pp.saveProfile, onToast]);

  async function submit() {
    if (busy || !collectRef.current) return;
    setBusy(true);
    setJustSaved(false);
    const fields = collectRef.current();
    const res = await pp.saveProfile(fields);
    setBusy(false);
    if (res.ok) {
      setJustSaved(true);
      setDirty(false);
      onToast(res.status === "applied" ? "Опубликовано" : "Отправлено на проверку у ОО");
      window.setTimeout(() => setJustSaved(false), 2500);
    } else {
      onToast(res.error ?? "Не удалось сохранить", true);
    }
  }

  const data = pp.data!;

  return (
    <>
      <ProfileTab
        initial={data.profile}
        media={data.media}
        news={data.news}
        onDirtyChange={setDirty}
        onAutoSave={autoSave}
        registerCollect={registerCollect}
        onUpload={(file, opts) => pp.uploadMedia(file, opts)}
        onSaveCrops={(id, crops) => pp.saveCrops(id, crops)}
        onSaveFilter={(id, filter) => pp.saveFilter(id, filter)}
        onSetFanOnly={(id, fan) => pp.setFanOnly(id, fan)}
        onSetFanPortrait={(id, fp) => pp.setFanPortrait(id, fp)}
        onDelete={(id) => pp.deleteMedia(id)}
        onSaveNews={pp.saveNews}
        onToast={onToast}
        view={view}
        restoreDraft={restoreDraft}
      />

      {/* Липкая панель: всегда видна внизу экрана, ничего доскролливать не нужно. */}
      <div className={css.saveBar}>
        <SaveStatusChip status={pp.saveStatus} compact pending={dirty} />
        <button type="button" className={`${css.btn} ${css.primary} ${justSaved ? css.sent : ""}`} disabled={busy} onClick={submit}>
          {justSaved ? <ACheck /> : <ASend />}
          {justSaved ? "Отправлено" : busy ? "…" : data.autoApprove ? "Опубликовать сейчас" : "Отправить на проверку"}
        </button>
      </div>
      {!data.autoApprove ? (
        <div className={css.savedHint} style={{ color: "var(--text-2)" }}>
          Правки сохраняются сами; кнопка — отправить версию в ОО на проверку.
        </div>
      ) : null}
    </>
  );
}

function SaveStatusChip({ status, compact = false, pending = false }: { status: "saved" | "saving" | "offline"; compact?: boolean; pending?: boolean }) {
  const label = status === "offline"
      ? "Нет связи — черновик сохранён, дошлём"
      : status === "saving" || pending
        ? "Сохраняю…"
      : "Сохранено";
  return (
    <span className={`${css.saveChip} ${css[status]} ${compact ? css.compact : ""}`} aria-live="polite">
      {label}
    </span>
  );
}

/* ── Status plaque ─────────────────────────────────────────────────────────── */
function StatusPlaque({ data }: { data: ProfileResponse }) {
  const { kind, label, note } = deriveStatus(data);
  return (
    <div className={`${css.status} ${css[kind]}`}>
      <span className={css.statusIco}><StatusIcon kind={kind} /></span>
      <span className={css.statusText}>
        <span className={css.statusLabel}>{label}</span>
        {note ? <span className={css.statusNote}>{note}</span> : null}
        {data.change?.submittedAt ? (
          <span className={css.statusMeta}>Отправлено: {fmtUtc5(data.change.submittedAt)}</span>
        ) : null}
      </span>
      {data.autoApprove ? <span className={css.autoTag}>Авто-одобрение</span> : null}
    </div>
  );
}

type StatusKind = "draft" | "pending" | "published" | "revision";

function deriveStatus(data: ProfileResponse): { kind: StatusKind; label: string; note: string | null } {
  const ch = data.change;
  if (ch) {
    if (ch.status === "pending") return { kind: "pending", label: "На проверке у ОО", note: "Правки ждут одобрения." };
    if (ch.status === "needs_revision") return { kind: "revision", label: "На доработке", note: ch.reviewNote || "ОО вернул на доработку." };
    if (ch.status === "rejected") return { kind: "revision", label: "Отклонено", note: ch.reviewNote || "Правки отклонены — можно начать заново." };
    if (ch.status === "approved") return { kind: "published", label: "Опубликовано", note: "Карточка видна клиентам." };
  }
  if (data.isDraft) return { kind: "draft", label: "Черновик", note: "Заполни и отправь на проверку." };
  return { kind: "published", label: "Опубликовано", note: "Карточка видна клиентам." };
}

function StatusIcon({ kind }: { kind: StatusKind }) {
  if (kind === "published") return <ACheck />;
  if (kind === "pending") return <ASend />;
  // draft / revision share the document glyph
  return <AProfile />;
}

/** Format an epoch (seconds or ms) as a UTC+5 business timestamp. */
function fmtUtc5(epoch: number): string {
  const ms = epoch < 1e12 ? epoch * 1000 : epoch;
  const d = new Date(ms + 5 * 3_600_000);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}.${p(d.getUTCMonth() + 1)} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}
